import { UserManager, WebStorageStateStore, User } from "oidc-client";
import AppSettings from "../configs/appSettings";
import Constants from "../helpers/Constants";

export class AuthorizeService {
  private _callbacks: any[] = [];
  private _nextSubscriptionId = 0;
  private _user!: User;
  //private _isAuthenticated = false;

  private _userManager: UserManager;

  constructor() {
    var link = document.createElement("a");
    link.href = window.location.href;
    const url = `${link.protocol}//${link.host}`;
    const settings = {
      authority: AppSettings.SSOAuthority,
      client_id: Constants.OAuthOptions.ClientId,
      redirect_uri: `${url}${Constants.ApplicationPaths.LoginCallback}`,
      post_logout_redirect_uri: `${url}${Constants.ApplicationPaths.LogOutCallback}`,
      response_type: "code",
      scope: Constants.OAuthOptions.ClientScope,
      automaticSilentRenew: false,
      //includeIdTokenInSilentRenew: true,
      userStore: new WebStorageStateStore({
        prefix: Constants.OAuthOptions.ClientId,
        //store: window.sessionStorage,
      }),
      monitorSession: false,
    };
    this._userManager = new UserManager(settings);
    this._userManager.events.addUserSignedOut(async () => {
      await this._userManager.removeUser();
      this.updateState(undefined);
    });
  }

  async isAuthenticated() {
    const user = await this.getUser();
    return !!user;
  }

  async getUser() {
    if (this._user && this._user.profile) {
      return this._user.profile;
    }
    const user = await this._userManager.getUser();
    return user && user.profile;
  }

  async getAccessToken() {
    const user = await this._userManager.getUser();
    return user && user.access_token;
  }

  async signIn(state: any) {
    // try {
    //   const silentUser = await this._userManager.signinSilent(
    //     this.createArguments(state)
    //   );
    //   this.updateState(silentUser);
    //   return this.success(state);
    // } catch (silentError: any) {
    //   console.log("Silent authentication error: ", silentError);
    try {
      const args = this.createArguments(state);
      await this._userManager.signinRedirect(args);
      return this.redirect();
    } catch (redirectError: any) {
      console.log("Redirect authentication error: ", redirectError);
      return this.error(redirectError.message);
    }
    // }
  }

  async completeSignIn(url: string) {
    try {
      const user = await this._userManager.signinCallback(url);
      this.updateState(user);
      return this.success(user && user.state);
    } catch (error) {
      console.log("There was an error signing in: ", error);
      return this.error("There was an error signing in.");
    }
  }

  async signOut(state: any) {
    try {
      await this._userManager.signoutRedirect(this.createArguments(state));
      return this.redirect();
    } catch (redirectSignOutError: any) {
      console.log("Redirect signout error: ", redirectSignOutError);
      return this.error(redirectSignOutError.message);
    }
  }

  async completeSignOut(url: string) {
    try {
      const response = await this._userManager.signoutCallback(url);
      this.updateState(null);
      return this.success(response && response.state);
    } catch (error: any) {
      console.log(`There was an error trying to log out '${error}'.`);
      return this.error(error.message);
    }
  }

  updateState(user: any) {
    this._user = user;
    //this._isAuthenticated = !!this._user;
    this.notifySubscribers();
  }

  subscribe(callback: any) {
    this._callbacks.push({
      callback,
      subscription: this._nextSubscriptionId++,
    });
    return this._nextSubscriptionId - 1;
  }

  unsubscribe(subscriptionId: number) {
    const subscriptionIndex = this._callbacks
      .map((element, index: any) =>
        element.subscription === subscriptionId
          ? { found: true, index }
          : { found: false }
      )
      .filter((element) => element.found === true);
    if (subscriptionIndex.length !== 1) {
      throw new Error(
        `Found an invalid number of subscriptions ${subscriptionIndex.length}`
      );
    }

    this._callbacks.splice(subscriptionIndex[0].index, 1);
  }

  notifySubscribers() {
    for (let i = 0; i < this._callbacks.length; i++) {
      const callback = this._callbacks[i].callback;
      callback();
    }
  }

  createArguments(state: any) {
    return { useReplaceToNavigate: true, data: state };
  }

  error(message: string) {
    return { status: AuthenticationResultStatus.Fail, message, state: null };
  }

  success(state: any) {
    return { status: AuthenticationResultStatus.Success, state, message: "" };
  }

  redirect() {
    return {
      status: AuthenticationResultStatus.Redirect,
      state: null,
      message: "",
    };
  }

  static get instance() {
    return authService;
  }
}

const authService = new AuthorizeService();

export default authService;

export const AuthenticationResultStatus = {
  Redirect: "redirect",
  Success: "success",
  Fail: "fail",
};
